import type { Color } from "@/lib/types";

/** The basic lands the background can paint. "wastes" is the colorless variant. */
export type Land = "plains" | "island" | "swamp" | "mountain" | "forest" | "wastes";

export const LANDS: readonly Land[] = ["plains", "island", "swamp", "mountain", "forest", "wastes"];

/** WUBRG order, used as the default journey when a page declares nothing. */
export const JOURNEY: readonly Land[] = ["plains", "island", "swamp", "mountain", "forest"];

const LAND_OF_COLOR: Readonly<Record<Color, Land>> = {
  W: "plains",
  U: "island",
  B: "swamp",
  R: "mountain",
  G: "forest",
};

/** Lands for a color identity; colorless identities get Wastes. */
export function landsForColors(colors: readonly Color[]): Land[] {
  if (colors.length === 0) return ["wastes"];
  return colors.map((color) => LAND_OF_COLOR[color]);
}

export function isLand(value: unknown): value is Land {
  return typeof value === "string" && (LANDS as readonly string[]).includes(value);
}

/** Static fallback when WebGL is unavailable: a dark, graded gradient per land. */
export const FALLBACK_GRADIENTS: Readonly<Record<Land, string>> = {
  plains:
    "radial-gradient(ellipse 70% 40% at 30% 58%, rgb(196 128 58 / 0.42), transparent 70%), linear-gradient(180deg, #0b0a0c 0%, #3a2318 52%, #2a1a0c 64%, #070708 100%)",
  island:
    "radial-gradient(ellipse 70% 35% at 55% 55%, rgb(90 150 190 / 0.32), transparent 70%), linear-gradient(180deg, #070b1a 0%, #142a3e 50%, #0a1a28 60%, #060812 100%)",
  swamp:
    "radial-gradient(circle at 28% 22%, rgb(180 200 210 / 0.22), transparent 30%), linear-gradient(180deg, #06070d 0%, #111a1a 52%, #0a100e 60%, #050506 100%)",
  mountain:
    "radial-gradient(ellipse 50% 30% at 50% 60%, rgb(230 90 30 / 0.35), transparent 70%), linear-gradient(180deg, #0d0509 0%, #2a0c0c 50%, #160808 64%, #060506 100%)",
  forest:
    "radial-gradient(ellipse 50% 60% at 25% 10%, rgb(170 200 120 / 0.22), transparent 70%), linear-gradient(180deg, #06100b 0%, #0f2418 50%, #0a160e 70%, #050809 100%)",
  wastes:
    "radial-gradient(ellipse 70% 35% at 50% 58%, rgb(170 150 170 / 0.22), transparent 70%), linear-gradient(180deg, #0b0910 0%, #241f28 55%, #17131a 66%, #070708 100%)",
};
